/**
 * 확인 필요 의약품 · 생성 모달 — 왼쪽 기초정보 패널 행 빌더.
 */

import type { SettlementRow } from '@/pages/drugs/drugCreateFormUtils';

export interface PanelRow {
  label: string;
  value: string;
  multiline?: boolean;
}

export function normalizeRefYm(refYm: unknown): string {
  if (refYm == null) return '';
  return String(refYm);
}

import { formatYearMonth } from '@/shared/lib/dateFormat';

export function formatRefYmDisplay(refYm: unknown): string {
  const s = normalizeRefYm(refYm);
  if (s.length < 6) return '-';
  return formatYearMonth(s);
}

export function settlementFeeDrugCode(settlementRow: SettlementRow): string {
  return (
    settlementRow.pharmaDrugCode ??
    settlementRow.drugCodePharma ??
    settlementRow.drugCode ??
    ''
  ).toString();
}

function displayPanelValue(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  return String(value);
}

function displayPanelMultiline(value: unknown, delimiter = ','): string {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  const parts = String(value)
    .split(delimiter)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return '-';
  if (parts.length === 1) return parts[0];
  return parts.join('\n');
}

export function resolveMfdsItemSeq(referenceRow: Record<string, unknown>): string {
  return String(referenceRow.itemSeq ?? '');
}

export function resolveMfdsItemName(referenceRow: Record<string, unknown>): string {
  return String(referenceRow.itemName ?? referenceRow.drugName ?? '');
}

export function resolveMfdsEtcOtc(referenceRow: Record<string, unknown>): string {
  return String(
    referenceRow.etcOtcTypeShort ??
      referenceRow.etcOtcCode ??
      referenceRow.etcOtcType ??
      '',
  );
}

export function resolveMappingDrugCodeHira(referenceRow: Record<string, unknown>): string {
  return String(referenceRow.drugCodeHira ?? referenceRow.drugCode ?? '');
}

export function resolveMappingDrugCodeMfds(referenceRow: Record<string, unknown>): string {
  return String(referenceRow.drugCodeMfds ?? referenceRow.itemSeq ?? '');
}

export function resolveMappingFormDisplay(referenceRow: Record<string, unknown>): string {
  const dosage = referenceRow.dosageFormType;
  const pkg = referenceRow.packageType;
  if (dosage && pkg) {
    return `${dosage} / ${pkg}`;
  }
  return String(dosage || pkg || '');
}

export function resolveMappingEtcOtc(referenceRow: Record<string, unknown>): string {
  return String(
    referenceRow.etcOtcTypeShort ??
      referenceRow.etcOtcType ??
      referenceRow.etcOtcCode ??
      '',
  );
}

export function buildLicenseLeftPanelRows(
  refYm: string,
  settlementRow: SettlementRow,
  referenceRow: Record<string, unknown>,
): PanelRow[] {
  const feeCode = settlementFeeDrugCode(settlementRow);
  const packUnit = referenceRow.packUnit;
  return [
    { label: '기준 년 월', value: formatRefYmDisplay(refYm) },
    { label: '의약품 명', value: displayPanelValue(settlementRow.drugName) },
    { label: '의약품 코드', value: displayPanelValue(feeCode) },
    { label: '제약사 약품 코드', value: displayPanelValue(feeCode) },
    { label: '품목 기준 코드', value: displayPanelValue(resolveMfdsItemSeq(referenceRow)) },
    {
      label: '포장단위 - 규격',
      value: displayPanelMultiline(packUnit, ','),
      multiline: true,
    },
    {
      label: '포장단위 - 단위',
      value: displayPanelMultiline(packUnit, ','),
      multiline: true,
    },
    { label: '급여상태', value: '-' },
    { label: '전문/일반 구분', value: displayPanelValue(resolveMfdsEtcOtc(referenceRow)) },
    { label: '정보구분', value: '-' },
    { label: '투여경로', value: '-' },
    { label: 'ATC 코드', value: displayPanelValue(referenceRow.atcCode ?? '-') },
  ];
}

export function buildMappingLeftPanelRows(
  refYm: string,
  settlementRow: SettlementRow,
  referenceRow: Record<string, unknown>,
): PanelRow[] {
  const feeCode = settlementFeeDrugCode(settlementRow);
  return [
    { label: '기준 년 월', value: formatRefYmDisplay(refYm) },
    { label: '의약품 명', value: displayPanelValue(settlementRow.drugName) },
    { label: '의약품 코드', value: displayPanelValue(resolveMappingDrugCodeHira(referenceRow)) },
    { label: '제약사 약품 코드', value: displayPanelValue(feeCode) },
    {
      label: '품목 기준 코드',
      value: displayPanelValue(resolveMappingDrugCodeMfds(referenceRow)),
    },
    { label: '함량 - 규격/단위', value: displayPanelValue(referenceRow.ingredientAmount) },
    {
      label: '제형 - 규격/단위',
      value: displayPanelValue(resolveMappingFormDisplay(referenceRow)),
    },
    { label: '급여상태', value: '-' },
    { label: '전문/일반 구분', value: displayPanelValue(resolveMappingEtcOtc(referenceRow)) },
    { label: '정보구분', value: '-' },
    { label: '투여경로', value: '-' },
    { label: 'ATC 코드', value: displayPanelValue(referenceRow.atcCode ?? '-') },
  ];
}

export function buildBasicLeftPanelRows(
  refYm: string,
  referenceRow: Record<string, unknown>,
): PanelRow[] {
  return [
    { label: '기준 년 월', value: formatRefYmDisplay(refYm) },
    { label: '의약품 명', value: displayPanelValue(referenceRow.drugName) },
    { label: '의약품 코드', value: displayPanelValue(referenceRow.drugCode) },
    { label: '제약사 약품 코드', value: displayPanelValue(referenceRow.drugCodePharma) },
    { label: '품목 기준 코드', value: displayPanelValue(referenceRow.drugCodeMfds) },
  ];
}

export function buildSettlementLeftPanelRows(
  refYm: string,
  settlementRow: SettlementRow,
  referenceRow?: Record<string, unknown> | null,
): PanelRow[] {
  const feeCode = settlementFeeDrugCode(settlementRow);
  const ref = referenceRow ?? {};
  return [
    { label: '기준 년 월', value: formatRefYmDisplay(refYm) },
    { label: '의약품 명', value: displayPanelValue(settlementRow.drugName) },
    { label: '의약품 코드', value: displayPanelValue(ref.drugCode ?? feeCode) },
    { label: '제약사 약품 코드', value: displayPanelValue(feeCode) },
    { label: '품목 기준 코드', value: displayPanelValue(ref.drugCodeMfds) },
    { label: '포장단위 - 규격', value: '-' },
    { label: '포장단위 - 단위', value: '-' },
    { label: '급여상태', value: '-' },
    { label: '전문/일반 구분', value: '-' },
    { label: '정보구분', value: '-' },
    { label: '투여경로', value: '-' },
    { label: 'ATC 코드', value: '-' },
  ];
}

/**
 * 기초정보 탭 — 선택 의약품 코드 복사 PATCH body.
 * 정산수수료 행의 제약사 약품코드를 기초정보 drugCode 행에 반영한다.
 */
export function buildBasicCopyPatchRequest(params: {
  refYm: string;
  settlementRow: SettlementRow;
  basicRow: { drugCode?: string };
}): { refYm: string; drugCode: string; body: Record<string, unknown> } {
  const { refYm, settlementRow, basicRow } = params;
  const drugCodePharma = settlementFeeDrugCode(settlementRow);
  return {
    refYm: normalizeRefYm(refYm),
    drugCode: basicRow.drugCode != null ? String(basicRow.drugCode) : '',
    body: {
      drugCodePharma: drugCodePharma || undefined,
      dataSourceType: 'ADMIN',
    },
  };
}

export function buildLeftPanelRows(
  refYm: string,
  settlementRow: SettlementRow,
  referenceRow: Record<string, unknown> | null | undefined,
  copyFlowTab: string | null,
): PanelRow[] {
  if (copyFlowTab === 'basic' && referenceRow) {
    return buildBasicLeftPanelRows(refYm, referenceRow);
  }
  if (copyFlowTab === 'license' && referenceRow) {
    return buildLicenseLeftPanelRows(refYm, settlementRow, referenceRow);
  }
  if (copyFlowTab === 'mapping' && referenceRow) {
    return buildMappingLeftPanelRows(refYm, settlementRow, referenceRow);
  }
  return buildSettlementLeftPanelRows(refYm, settlementRow, referenceRow);
}
